import { Product } from '../services/departures'
import { useCallback, useContext, useMemo } from 'react'
import { ProductsContext } from '../contexts/products'
import { SerializedPassengerInformation } from '../types/passenger-information'
import { priceMultiplier } from '../utils/products'

interface PassengerProductHooksOptions {
  passengerInformation: Omit<SerializedPassengerInformation, 'main'>
  bikes?: boolean
  diets?: boolean
  other?: boolean
  localCost?: boolean
  days: number
}

interface PassengerProductHookReturn {
  passengerProductIds: string[]
  groupedPassengerProducts: Map<Product, number>
  passengerProductEntries: Array<[Product, number]>
  passengerProductsTotalPrice: number
}

interface AdditionalProductsHookReturn {
  additionalProductEntries: Array<[Product, number]>
  additionalProductsTotalPrice: number
}

export function usePassengerProducts ({ passengerInformation, bikes = true, diets = true, other = true, localCost, days }: PassengerProductHooksOptions): PassengerProductHookReturn {
  const { productMap } = useContext(ProductsContext)

  const localCostIds = useMemo(() => Array.from(productMap.values()).filter(product => product.local_cost).map(product => product.id), [productMap])
  const productFilter = useCallback((productId: string) => productId !== undefined && (localCost === undefined || localCostIds.includes(productId) === localCost), [localCost, localCostIds])

  const passengerProductIds = useMemo(() => {
    let ids: string[] = []
    if (bikes) {
      const bikeIds = passengerInformation.passengers.map(passenger => passenger.bike).filter(productFilter)
      ids = ids.concat(bikeIds)
    }

    if (diets) {
      const dietIds = passengerInformation.passengers.map(passenger => passenger.diet).filter(productFilter)
      ids = ids.concat(dietIds)
    }

    if (other) {
      const otherIds = passengerInformation.passengers.flatMap(passenger => passenger.other).filter(productFilter)
      ids = ids.concat(otherIds)
    }

    return ids
  }, [bikes, diets, other, passengerInformation.passengers, productFilter])

  const groupedPassengerProducts = useMemo(() => {
    const map = new Map<Product, number>()

    passengerProductIds.forEach(id => {
      const product = productMap.get(id)

      if (product !== undefined) {
        map.set(product, (map.get(product) ?? 0) + 1)
      }
    })

    return map
  }, [passengerProductIds, productMap])

  const passengerProductEntries = useMemo(() => {
    return Array.from(groupedPassengerProducts.entries()).sort(([productA], [productB]) => {
      return (productA.base_name ?? productA.name).localeCompare(productB.base_name ?? productB.name)
    })
  }, [groupedPassengerProducts])

  const passengerProductsTotalPrice = useMemo(() => {
    return Array.from(groupedPassengerProducts.entries())
      .map(([product, count]) => product.unit_price * count * priceMultiplier(product.price_calculation, days))
      .reduce((a, b) => a + b, 0)
  }, [days, groupedPassengerProducts])

  return {
    passengerProductIds,
    groupedPassengerProducts,
    passengerProductEntries,
    passengerProductsTotalPrice
  }
}

export function useAdditionalProducts (additionalProducts: Record<string, number | string>, days: number, localCost?: boolean): AdditionalProductsHookReturn {
  const { productMap } = useContext(ProductsContext)

  const productFilter = useCallback(([product, amount]: [Product | undefined, number]) => product !== undefined && amount > 0 && (localCost === undefined || product.local_cost === localCost), [localCost])

  const allProductEntries: Array<[Product | undefined, number]> = useMemo(() => Object.entries(additionalProducts).map(([product, amount]) => {
    return [productMap.get(product), typeof amount === 'string' ? parseInt(amount, 10) : amount]
  }), [additionalProducts, productMap])

  const additionalProductEntries = useMemo(() => allProductEntries.filter(productFilter) as Array<[Product, number]>, [allProductEntries, productFilter])
  const additionalProductsTotalPrice = useMemo(() => {
    return additionalProductEntries.map(([product, amount]) => product.unit_price * amount * priceMultiplier(product.price_calculation, days)).reduce((a, b) => a + b, 0)
  }, [additionalProductEntries, days])

  return {
    additionalProductEntries,
    additionalProductsTotalPrice
  }
}
